<template>
  <div>
    <EventModal
      v-model:visible-event-modal="eventModalVisible"
      :event="issue"
      data-testId="CommonIssueModalConfirmButton"
      :issue-type="IssueType.common"
      :event-type="EventType.Issue"
      :title="
        modalTitle ??
        (!!event
          ? t('components.issueModal.updateIssue').toString()
          : t('components.issueModal.newIssue').toString())
      "
      :save-and-new-button-title="
        !!event
          ? t('components.issueModal.updateAndTask').toString()
          : t('components.issueModal.createAndTask').toString()
      "
      :original-owner-id="event?.owner?.id"
      @save="save"
      @observer:valid="observerValid = $event"
    >
    </EventModal>
    <CreateTaskModal
      v-if="issueForTask"
      v-model:visible-action-modal="taskModalVisible"
      :event="issueForTask"
      @save="taskModalVisible = false"
      @hide="taskModalVisible = false"
    />
  </div>
</template>

<script setup lang="ts">
import { useTranslation } from "@/language/translation";

import { provide, ref, type PropType, nextTick, inject, watch, computed } from "vue";
import { DateTime } from "luxon";
import { useStore } from "vuex";
import {
  BoardIdInjectionKey,
  DefaultDateInjectionKey,
  DefaultEisenhowerPriorityInjectionKey,
  DefaultFlagInjectionKey,
} from "@/views/event-action/EventActionBoardInjectionKeys";
import {
  EventType,
  type ICommonIssue,
  type IFlagConnection,
  useCreateCommonIssueMutation,
  useUpdateCommonIssueMutation,
  type IEventOption,
  IssueType,
  EisenhowerPriority,
  SQDCTileEventTypes,
} from "@/graphql";
import EventModal from "../../event/modal/EventModal.vue";
import CreateTaskModal from "../../task/modal/CreateTaskModal.vue";
import { CreateInjectionKey } from "../IssueTypes";
import { addEvent } from "@/graphql/apolloCacheUtils";
import type { EventEditType, IssueEditType } from "../../event/modal/EventEditType";
import { injectCustomAttributesForSBB } from "../customAttributesHelper";
import { cloneDeep } from "lodash";

const defaultIssue = (
  assigneeId: number,
  deadline: string,
  flags: IFlagConnection[],
  eisenhowerPriority: EisenhowerPriority,
  type: IssueType = IssueType.common,
): IssueEditType => ({
  assigneeId,
  deadline,
  flags,
  description: "",
  eisenhowerPriority,
  fileIds: [],
  type,
  __typename: "Issue",
  customAttributes: [],
});

defineOptions({
  inheritAttrs: true,
});
const props = defineProps({
  visible: {
    type: Boolean,
    default: false,
  },
  event: {
    type: Object as PropType<undefined | IssueEditType>,
    default: undefined,
  },
  modalTitle: {
    type: String,
    required: false,
    default: undefined,
  },
});
const emit = defineEmits([
  "saveIssue",
  "closeOnSave",
  "createEvent",
  "updateEvent",
  "save",
  "update:eventModalVisible",
]);
const observerValid = ref(false);
const store = useStore();

const eventModalVisible = ref(props.visible);
const taskModalVisible = ref(false);
const loginUserId = store.getters["login/isAnonym"] ? null : store.getters["login/userId"];
const defaultFlags = inject(DefaultFlagInjectionKey, ref([]));
const defaultEisenhowerPriority = inject(
  DefaultEisenhowerPriorityInjectionKey,
  ref(EisenhowerPriority.NO_PRIORITY),
);
const defaultDate = inject(
  DefaultDateInjectionKey,
  computed(() => ({ isoString: DateTime.now().toISO(), editable: true })),
);

const issue = ref<IssueEditType>(
  defaultIssue(
    loginUserId,
    defaultDate.value.isoString,
    defaultFlags.value,
    defaultEisenhowerPriority.value,
  ),
);

const issueForTask = ref<EventEditType | undefined>(props.event);

provide(
  CreateInjectionKey,
  computed(() => props?.event?.id == null),
);

const { mutate: createCommonIssueMutation } = useCreateCommonIssueMutation({});
const { mutate: updateCommonIssueMutation } = useUpdateCommonIssueMutation({});

const issueInputFromIssue = (): ICommonIssue => {
  return {
    deadline: issue.value.deadline,
    assigneeId: issue.value.assigneeId,
    flags: issue.value.flags.map((flag) => ({ id: flag.id })),
    description: issue.value.description,
    fileIds: issue.value.fileIds,
    eisenhowerPriority: issue.value.eisenhowerPriority,
    type: issue.value.type,
    customAttributes: issue.value.customAttributes?.map((el) => ({
      name: el.name,
      value: el.value,
      type: el.type,
      required: el.required,
    })),
  };
};

const boardId = inject(BoardIdInjectionKey, ref<number | null>(null));

const create = async () => {
  if (observerValid.value !== true || boardId.value == null) {
    return;
  }
  const newIssue = await createCommonIssueMutation(
    {
      boardId: boardId.value,
      issue: issueInputFromIssue(),
    },
    {
      update: (_cache, { data }) => {
        if (data) {
          addEvent(data.createCommonIssue, boardId.value!);
        }
      },
    },
  );
  if (newIssue?.data?.createCommonIssue) {
    issueForTask.value = {
      ...newIssue.data.createCommonIssue,
      fileIds: newIssue.data.createCommonIssue.Folder?.files.map((f) => f.id) ?? [],
    };
    emit("saveIssue", { id: newIssue.data.createCommonIssue.id });
    emit("save", { eventId: newIssue.data.createCommonIssue.id, eventType: EventType.Issue });
    await nextTick();
  }
};

const update = async () => {
  if (observerValid.value !== true) {
    return;
  }
  if (!props.event) {
    throw new Error("update called without set event");
  }
  await updateCommonIssueMutation({
    issueId: props.event.id!,
    issue: issueInputFromIssue(),
  });
  emit("saveIssue", { id: props.event.id });
};

const save = async (openTask: boolean) => {
  await (props.event ? update() : create());
  if (openTask === true) {
    eventModalVisible.value = false;
    taskModalVisible.value = true;
  } else {
    eventModalVisible.value = false;
  }
  const eventId = props.event?.id ?? issueForTask.value?.id;
  if (eventId == null) {
    throw Error("eventId is missing");
  }
  const eventIdentifier: IEventOption = {
    eventId,
    eventType: EventType.Issue,
  };
  emit(props.event ? "updateEvent" : "createEvent", eventIdentifier, openTask === true);
};

const show = (options?: { type: SQDCTileEventTypes }) => {
  let type = IssueType.common;
  switch (options?.type) {
    case SQDCTileEventTypes.SBBQuality:
      type = IssueType.SBBQuality;
      break;
  }
  issue.value = props.event
    ? {
        deadline: props.event.deadline,
        assigneeId: props.event.owner?.id ?? loginUserId,
        flags: props.event.flags,
        description: props.event.description,
        fileIds: props.event.Folder?.files.map((f) => f.id) ?? [],
        deletedAt: props.event.deletedAt,
        eisenhowerPriority: props.event.eisenhowerPriority,
        __typename: props.event.__typename,
        customAttributes: cloneDeep(props.event.customAttributes),
        type: props.event.type,
      }
    : defaultIssue(
        loginUserId,
        defaultDate.value.isoString,
        defaultFlags.value,
        defaultEisenhowerPriority.value,
        type,
      );

  if (
    props.event == null &&
    (options?.type === SQDCTileEventTypes.SBBFaultyPart ||
      options?.type === SQDCTileEventTypes.SBBSparePart ||
      options?.type === SQDCTileEventTypes.SBBAndon ||
      options?.type === SQDCTileEventTypes.SBBAccident)
  ) {
    injectCustomAttributesForSBB(issue.value, options?.type);
  }
  eventModalVisible.value = true;
};
watch(eventModalVisible, () => {
  emit("update:eventModalVisible", eventModalVisible.value);
});

defineExpose({
  show,
  save,
});

const { t } = useTranslation();
</script>

<style lang="scss" scoped></style>
